// features/cx/reconnection/hooks.ts

import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useState, useCallback } from "react";
import { reconnectionApi } from "./api";
import { toast } from "@shared/ui/use-toast";
import {
  ReconnectionFilters,
  CreateReconnectionRequestPayload,
  UpdateReconnectionRequestPayload,
  UpdateConsumerPlanPayload,
  ReconnectActivateConsumerPayload,
  BulkReconnectionOperation,
} from "./types";
import { disconnectionApi } from "../disconnect/api";
import { ConsumerSearchFilters } from "../disconnect/types";
import { useNavigate } from "react-router-dom";
import { UtilityConfigFilters } from "../shared/types/consumerRequest";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { queryClient } from "@shared/api/queries/queryClient";

// ================================
// RECONNECTION REQUEST HOOKS
// ================================

/**
 * Get reconnection requests list with tab support (Current/History)
 */
export const useReconnectionRequestsWithTab = (
  remoteUtilityId: number,
  activeTab: "Current" | "History",
  additionalFilters: Partial<ReconnectionFilters> = {}
) => {
  const filters: ReconnectionFilters = {
    remoteUtilityId,
    activeTab,
    requestType: "Reconnection",
    page: 1,
    limit: 50,
    ...additionalFilters,
  };

  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.list(filters),
    () => reconnectionApi.getReconnectionRequests(filters),
    {
      enabled: !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get reconnection requests list
 */
export const useReconnectionRequests = (filters: ReconnectionFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.list(filters),
    () => reconnectionApi.getReconnectionRequests(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get reconnection request detail
 */
export const useReconnectionRequestDetail = (requestId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.details(String(requestId)),
    () => reconnectionApi.getReconnectionRequestDetail(requestId),
    {
      enabled: !!requestId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get reconnection summary
 */
export const useReconnectionSummary = (params: {
  remoteUtilityId: number;
  requestType: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.summary(params),
    () => reconnectionApi.getReconnectionSummary(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// PLANS HOOKS
// ================================

/**
 * Get plans from onboarding API
 */
export const usePlans = (remoteUtilityId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.plans(remoteUtilityId),
    () => reconnectionApi.getPlans(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ================================
// RECONNECTION MUTATION HOOKS
// ================================

/**
 * Create reconnection request
 */
export const useCreateReconnectionRequest = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreateReconnectionRequestPayload) =>
      reconnectionApi.createReconnectionRequest(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: (data: any, variables: any) => {
        toast({
          title: "Success",
          description: "Reconnection request created successfully!",
          variant: "default",
        });
        navigate("/cx/reconnections/success", {
          state: { ...data, formData: variables },
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to create reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update reconnection request
 */
export const useUpdateReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    (payload: UpdateReconnectionRequestPayload) =>
      reconnectionApi.updateReconnectionRequest(requestId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          String(requestId)
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to update reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Delete reconnection request
 */
export const useDeleteReconnectionRequest = () => {
  return useSmartMutation(
    (requestId: number) => reconnectionApi.deleteReconnectionRequest(requestId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request deleted successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to delete reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update consumer plan
 */
export const useUpdateConsumerPlan = (requestId: number) => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: number;
      payload: UpdateConsumerPlanPayload;
    }) => reconnectionApi.updateConsumerPlan(consumerId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Consumer plan updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to update consumer plan: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Reconnect/activate consumer
 */
export const useReconnectActivateConsumer = (requestId: string) => {
  const navigate = useNavigate();
  return useSmartMutation(
    ({ requestId, payload }: { requestId: string; payload: any }) =>
      reconnectionApi.reconnectActivateConsumer(requestId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(requestId),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Consumer reconnected and activated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "reconnectionRequests"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "reconnectionRequests", "list"],
        });
        navigate(`/cx/reconnections?tab=history`);
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to reconnect/activate consumer: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Bulk update reconnection requests
 */
export const useBulkUpdateReconnectionRequests = () => {
  return useSmartMutation(
    (operation: BulkReconnectionOperation) =>
      reconnectionApi.bulkUpdateReconnectionRequests(operation),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Bulk operation completed successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to complete bulk operation: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// STATUS CHANGE HOOKS
// ================================

/**
 * Approve reconnection request
 */
export const useApproveReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    ({ requestId, remarks }: { requestId: number; remarks?: string }) =>
      reconnectionApi.approveReconnectionRequest(requestId, remarks),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request approved successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to approve reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Reject reconnection request
 */
export const useRejectReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    ({
      requestId,
      remarks,
      closeDate,
    }: {
      requestId: number;
      remarks: string;
      closeDate: string;
    }) =>
      reconnectionApi.rejectReconnectionRequest(requestId, remarks, closeDate),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request rejected successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to reject reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Acknowledge reconnection request
 */
export const useAcknowledgeReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    (requestId: number) =>
      reconnectionApi.acknowledgeReconnectionRequest(requestId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request acknowledged successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to acknowledge reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Hold reconnection request
 */
export const useHoldReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    ({ requestId, remarks }: { requestId: number; remarks?: string }) =>
      reconnectionApi.holdReconnectionRequest(requestId, remarks),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request put on hold successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to hold reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Resolve reconnection request
 */
export const useResolveReconnectionRequest = (requestId: number) => {
  return useSmartMutation(
    ({ requestId, closeDate }: { requestId: number; closeDate: string }) =>
      reconnectionApi.resolveReconnectionRequest(requestId, closeDate),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection request resolved successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to resolve reconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Complete reconnection workflow
 */
export const useCompleteReconnectionWorkflow = (requestId: number) => {
  return useSmartMutation(
    (params: {
      requestId: number;
      consumerId: number;
      planId?: number;
      meterData?: Record<string, any>;
    }) => reconnectionApi.completeReconnectionWorkflow(params),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.reconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.reconnectionRequests.list(),
        QueryKeyFactory.module.cx.reconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Reconnection workflow completed successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to complete reconnection workflow: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// CUSTOM HOOKS FOR COMPLEX FLOWS
// ================================

/**
 * Custom hook for managing reconnection filters and pagination
 */
export const useReconnectionFilters = (initialFilters: ReconnectionFilters) => {
  const [filters, setFilters] = useState<ReconnectionFilters>({
    page: 1,
    limit: 50,
    ...initialFilters,
  });

  const updateFilters = useCallback(
    (newFilters: Partial<ReconnectionFilters>) => {
      setFilters((prev) => ({
        ...prev,
        ...newFilters,
        page: newFilters.page || 1,
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 50,
      ...initialFilters,
    });
  }, [initialFilters]);

  const nextPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }));
  }, []);

  const prevPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max((prev.page || 1) - 1, 1),
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max(page, 1),
    }));
  }, []);

  const queryResult = useReconnectionRequests(filters);

  return {
    filters,
    updateFilters,
    resetFilters,
    nextPage,
    prevPage,
    setPage,
    ...queryResult,
  };
};

/**
 * Custom hook for bulk reconnection selection
 */
export const useBulkReconnectionSelection = () => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  }, []);

  const selectAll = useCallback((ids: number[]) => {
    setSelectedIds(ids);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const isSelected = useCallback(
    (id: number) => {
      return selectedIds.includes(id);
    },
    [selectedIds]
  );

  return {
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
  };
};

export const useReconnectionStatus = (params: { remoteUtilityId: string }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.status(), // Include params in query key for caching
    () => reconnectionApi.getStatus(params)
  );
};

// Hook for setup progress (original)
export const useReconnectionCategory = (params: {
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.category(), // Include params in query key for caching
    () => reconnectionApi.getCategory(params)
  );
};

/**
 * Search consumers for disconnection
 */
export const useConsumerSearch = (filters: ConsumerSearchFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerSearch(filters),
    () => reconnectionApi.searchConsumer(filters),
    {
      enabled: filters.searchData.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

/**
 * Get consumer meta data
 */
export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerMetaData(params),
    () => reconnectionApi.getConsumerMetaData(params),
    {
      enabled: !!params.consumerId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Get utility request configuration
 */
export const useUtilityRequestConfiguration = (
  filters: UtilityConfigFilters
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.utilityConfig(filters),
    () => reconnectionApi.getUtilityRequestConfiguration(filters),
    {
      enabled: !!filters.remoteUtilityId && !!filters.requestType,
      staleTime: 10 * 60 * 1000,
    }
  );
};

/**
 * Get time slot choices
 */
export const useTimeSlotChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.timeSlotChoices(),
    () => reconnectionApi.getTimeSlotChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const useMeterHistory = (consumerId: number, action: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.reconnectionRequests.meterHistory(consumerId),
    () => reconnectionApi.getMeterHistory(consumerId, remoteUtilityId),
    {
      enabled: !!consumerId,
    }
  );
};
